import { memo, useState } from "react";
import { Box, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { PrimaryBackgroundButton, PrimaryBorderButton, ThirdBackgroundButton } from "#components";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tier from "../components/Tier.jsx";
import theme from "../theme.js";
import { useNavigate } from "../router.js";
import companionLogo from "../assets/images/tiers/CompanionForDevelopers.png";
import panoramaLogo from "../assets/images/tiers/PanoramaforTeamleads.png";
import { teamTiers as tiers } from "../utils/team-tiers.js";

const panoramaFeatures = ["Automated Code Analysis", "Quality Monitoring", "Early Alerting", "Quality Gates", "Team Analytics"];
const companionFeatures = ["Source Code Analysis", "Quality Gates", "Cyclopt Notifications", "Cyclopt Profile", "Cyclopt API", "Cyclopt Plugins", "Cyclopt MCP Server", "Automated Code Fixing"];
const muted = "#9eb3c5";
const contactUrl = "https://cyclopt.com/contact";

const IncludedProduct = ({ name, features, logo }) => (
	<Box>
		<Box component="h3" sx={{ m: 0, mb: 3 }}>
			<Box component="img" src={logo} alt={`Cyclopt ${name}`} sx={{ display: "block", width: "100%", height: "110px", objectFit: "contain", objectPosition: "left center" }} />
		</Box>
		<Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
			{features.map((feature, index) => (
				<Box component="li" key={feature} sx={{ display: "flex", gap: 1, mb: 1.25 }}>
					<CheckIcon sx={{ color: theme.palette.pink.main, fontSize: "16px", mt: "2px", flexShrink: 0 }} />
					<Box>
						<Typography sx={{ fontSize: "13px", color: "#263440" }}>{feature}</Typography>
						{index === 0 && <Typography sx={{ color: muted, fontSize: "11px", mt: 0.5 }}>Maintainability · Security · Performance</Typography>}
					</Box>
				</Box>
			))}
		</Box>
	</Box>
);

const TeamPlan = () => {
	const navigate = useNavigate();
	const [selectedTier, setSelectedTier] = useState(1);
	const plan = tiers.find(({ tier }) => tier === selectedTier);
	const ContactSalesButton = plan.customPlan ? PrimaryBackgroundButton : PrimaryBorderButton;
	const buttonStyle = { width: "100%", borderRadius: "24px", textTransform: "none", fontWeight: 700, fontSize: "13px", minHeight: "44px" };

	return (
		<Box sx={{
			width: "100%", maxWidth: "1036px", mx: "auto", px: { xs: 2.5, md: "38px" }, pt: 2, pb: 5, boxSizing: "border-box",
			fontFamily: theme.typography.fontFamily,
			"& .MuiTypography-root, & .MuiButton-root": { fontFamily: "inherit" },
		}}>
			<Box sx={{ textAlign: "center", mb: 4.5 }}>
				<Typography component="h1" sx={{ color: theme.palette.primary.main, fontWeight: 700, fontSize: { xs: "27px", sm: "32px" }, letterSpacing: "-1px" }}>Configure your team plan</Typography>
				<Typography sx={{ color: muted, fontSize: "14px", mt: 1 }}>Choose your codebase size. Get the full Cyclopt toolkit.</Typography>
				{/* <Typography sx={{ color: theme.palette.primary.main, fontSize: "14px", fontWeight: 700, mt: 1 }}>UNLIMITED USERS, UNLIMITED REPOSITORIES, UNLIMITED ANALYSES</Typography> */}
				<Typography sx={{ color: theme.palette.primary.main, fontSize: "18px", fontWeight: 750, mt: 1 }}>Unlimited Users, Unlimited Repositories, Unlimited Analyses</Typography>
			</Box>
			<Box sx={{
				position: "relative", isolation: "isolate", py: 3,
				"&::before": {
					content: "\"\"", position: "absolute", inset: "0 calc(50% - 50vw)",
					bgcolor: theme.palette.primary.main, zIndex: -1,
				},
			}}>
				<Box component="fieldset" sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
					<Typography component="legend" sx={{ color: "#d13173", fontSize: "22px", fontWeight: 700 }}>How much code do you manage?</Typography>
					<Typography sx={{ color: theme.palette.grey.light, fontSize: "16px", mt: 0.75, mb: 2.25 }}>Choose the tier that covers your total lines of code.</Typography>
					<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 1.5 }}>
						{tiers.map((tier) => <Tier key={tier.tier} {...tier} selected={selectedTier === tier.tier} onSelect={() => setSelectedTier(tier.tier)} />)}
					</Box>
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1.5 }}>
					<Typography sx={{ color: theme.palette.grey.light, fontSize: "16px" }}>Lines of code are counted once per repository.</Typography>
					<Tooltip
						title="Your total codebase size is calculated by combining the lines of code from the largest branch of each repository, no matter how many analyses you run."
						arrow
						componentsProps={{
							tooltip: {
								sx: {
									maxWidth: "min(380px, calc(100vw - 32px))",
									p: 2,
									fontFamily: theme.typography.fontFamily,
									fontSize: "13px",
									lineHeight: 1.6,
									whiteSpace: "normal",
									overflowWrap: "anywhere",
								},
							},
						}}
					>
						<IconButton size="small" aria-label="How lines of code are counted" sx={{ color: "common.white" }}>
							<InfoOutlinedIcon sx={{ fontSize: "17px", color: "common.white" }} />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>
			<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: { xs: 4, md: 6 }, alignItems: "start", mt: 4 }}>
				<Box>
					<Typography sx={{ color: muted, fontSize: "12px", fontWeight: 700, letterSpacing: "1px", mb: 2.5 }}>INCLUDED IN EVERY TIER</Typography>
					<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: { xs: 3, sm: 6 } }}>
						<IncludedProduct name="Panorama" features={panoramaFeatures} logo={panoramaLogo} />
						<IncludedProduct name="Companion" features={companionFeatures} logo={companionLogo} />
					</Box>
				</Box>
				<Box component="aside" aria-label="Your plan summary" sx={{ bgcolor: "#f0f7fa", borderRadius: "12px", p: 3 }}>
					<Typography component="h2" sx={{ color: theme.palette.primary.main, fontWeight: 600, fontSize: "18px", mb: 3 }}>Your plan</Typography>
					<Box aria-live="polite" aria-atomic="true">
						<Box display="flex" justifyContent="space-between" sx={{ fontSize: "13px", mb: 2 }}><Box>Team plan</Box><Box component="strong">Tier {plan.tier}</Box></Box>
						<Box display="flex" justifyContent="space-between" sx={{ fontSize: "13px" }}><Box>Code allowance</Box><Box component="strong">{plan.allowance}</Box></Box>
						<Divider sx={{ my: 3, borderColor: "#d5e1eb" }} />
						<Typography sx={{ fontSize: "14px", color: muted }}>{plan.customPlan ? "Tailored to your team" : "Monthly total"}</Typography>
						<Typography sx={{ color: theme.palette.primary.main, fontSize: plan.customPlan ? "26px" : "34px", fontWeight: 700, lineHeight: 1.2 }}>
							{plan.customPlan ? "Contact sales" : <>€{plan.price}<Box component="span" sx={{ color: muted, fontSize: "12px", fontWeight: 400 }}> / month</Box></>}
						</Typography>
						{!plan.customPlan && <Typography sx={{ color: muted, fontSize: "12px", mt: 1.5 }}>+ VAT if applicable</Typography>}
					</Box>
					{!plan.customPlan && (
						<Box sx={{ mt: 2 }}>
							<ThirdBackgroundButton title="Proceed to checkout" width="100%" variant="contained" onClick={() => navigate(`../checkout?tier=${plan.tier}`)} sx={{ ...buttonStyle, bgcolor: theme.palette.pink.main, "&:hover": { bgcolor: theme.palette.third.dark } }} />
						</Box>
					)}
					<Box sx={{ mt: plan.customPlan ? 3 : 1 }}>
						<ContactSalesButton
							id="team-contact-sales-button"
							width="100%"
							title="Contact sales"
							onClick={() => window.location.assign(contactUrl)}
						/>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default memo(TeamPlan);
